import { useEffect, useState } from 'react'

import {
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiPagination,
	EuiPanel,
} from '@equipmentshare/ds2'

import StyledHeader from '../../components/StyledHeader'
import SearchBar from '../../components/SearchBar'
import RefreshButton from '../../components/RefreshButton'
import Table from '../../components/Table'
import PageTitle from 'views/components/PageTitle/PageTitle'
import PermissionsCheck from 'components/PermissionsCheck'

import { usePartsQuery, Permission, Part } from '../../../graphql'
import getColumns from './columns'
import { useDispatchSetSelectedModal } from '../../../contexts/modal-context/modal-context'
import { getAddModal, getEditModal, getMergeModal } from './components/action-modals/action-modals'
import usePermissions from 'hooks/usePermissions'
import { checkPermissions } from 'components/PermissionsCheck/helpers'
import { TABLE_HEIGHT } from '../../helpers/utils'
import { heapEvent } from '@equipmentshare/heaper'

export default function Parts(): JSX.Element {
	const { userPermissions } = usePermissions( [ Permission.CanEditPart ] )
	const canEdit = checkPermissions( userPermissions, [ Permission.CanEditPart ] )
	const paginationSize = 40
	const [ activePage, setActivePage ] = useState( 0 )
	const [ search, setSearch ] = useState( '' )
	const { loading, data, refetch } = usePartsQuery( {
		variables: {
			search,
			size: paginationSize,
			offset: activePage * paginationSize,
		}
	} )

	// Set back to first page
	useEffect( () => {
		setActivePage( 0 )
	}, [ search ] )// any change EXCEPT for activePage, offset, size
	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	const showPagination = ( !loading && !!( data?.parts?.length || activePage > 0 ) )

	const setSelectedModal = useDispatchSetSelectedModal()
	const onSuccess = async() => await refetch()

	const openAddModal = () => {
		heapEvent( 'Inventory - Parts Catalog - Click - Add Part', {} )
		setSelectedModal( getAddModal( onSuccess ) )
	}

	const openMergeModal = ( part: Part ) => {
		heapEvent( 'Inventory - Edit Part Modal - Click - Merge Parts', {} )
		setSelectedModal( getMergeModal( part, onSuccess ) )
	}
	const openEditModal = ( part: Part ) => {
		heapEvent( 'Inventory - Parts Catalog - Click - Edit', {} )
		setSelectedModal( getEditModal( part, openMergeModal ) )
	}

	const columns = getColumns( canEdit, openEditModal )

	const getNoItemsMessage = () => {
		if ( loading ) {
			return 'Loading data'
		}
		if ( data?.parts?.length === 0 && activePage > 0 ) {
			return 'No more records'
		}

		return 'No records found'
	}

	const onSearchChange = ( s: string ) => {
		heapEvent( 'Inventory - Parts Catalog - Change - Search Input', { search_term: s } )
		setSearch( s )
	}

	const fullHeight = 'eui-fullHeight'

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody className={fullHeight}>
				<PageTitle title="Product Catalog" />
				<StyledHeader>
					<EuiFlexGroup wrap>
						<EuiFlexItem style={{ minWidth: 677 }}>
							<div className="content searchFilter">
								<SearchBar search={search} setSearch={onSearchChange} />
								<RefreshButton refetchFunction={refetch} />
							</div>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<div className="content actions">
								<PermissionsCheck permissionsToCheck={[ Permission.CanCreatePart ]}>
									<EuiButton fill onClick={openAddModal}>Add product</EuiButton>
								</PermissionsCheck>
							</div>
						</EuiFlexItem>
					</EuiFlexGroup>
				</StyledHeader>

				<EuiPageSection className={fullHeight} paddingSize="none">
					<EuiPanel paddingSize="none" className="eui-yScroll" hasShadow={false} style={{ height: TABLE_HEIGHT }}>
						<Table
							isLoading={loading}
							emptyStateText={getNoItemsMessage()}
							columns={columns}
							rows={data?.parts}
						/>
					</EuiPanel>
					{ showPagination && (
						<EuiFlexGroup justifyContent="flexEnd">
							<EuiFlexItem grow={false}>
								<EuiPagination
									pageCount={0}
									activePage={activePage}
									onPageClick={( ap ) => setActivePage( ap )}
								/>
							</EuiFlexItem>
						</EuiFlexGroup>
					) }
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
